import type { ColumnDef, Getter, SortingFn } from "@tanstack/react-table";
import type { AxiosError } from "axios";
import type { TFunction } from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ButtonWithConfirmDialog,
  DeleteButton,
  EditButton,
  PrimaryButtonMediumV2,
} from "../../../../../components/Buttons/Buttons";
import { IconGroup } from "../../../../../components/DocumentCard/DocumentCard";
import { SlideOut } from "../../../../../components/SlideOut/SlideOut";
import { CustomTooltip, Table } from "../../../../../components/Table/Table";
import type { UUID } from "../../../../../types/types";
import type {
  AttributeSchema,
  CollectionValueRowSchema,
  ProductReference,
  ObjectReferenceResult,
  TemplateProductFormStepItemSchema,
} from "../../../../../types/types.PIM";
import { useRoutePath } from "../../../../../util/Routing";
import { rowHover, TablePlaceholder } from "../../../../../util/util";
import { H5 } from "../../../../../components/Typography/Typography";
import styled, { useTheme } from "styled-components";
import { AddItemToCollectionForm } from "./AddItemToCollectionForm";
import { CollectionValues } from "./NewProductForm";
import { PlusIcon } from "../../../../../components/Icons/Icons";
import { createPortal } from "react-dom";
import { IRow } from "../../../../admin/SellerAdmin/PIM/components/ProductCollectionSchemaTable/type";
import { getAttributeValue } from "../../../../admin/SellerAdmin/PIM/components/ProductCollectionSchemaTable/ProductCollectionSchema.util";
import {
  CollectionAttributeDisplayValue,
  DocumentCollectionValue,
  getCollectionAttributeDisplayValue,
} from "../../../../../util/collectionAttributeDisplayValue";
import { DocumentMoreModal } from "../../../../../components/AttributeGroupFormGenerator/DocumentAttribute/DocumentMoreModal";
import { ObjectReferenceMoreModal } from "../../../../../components/ObjectReference/ObjectReferenceMoreModal";
import { ProductCardsMoreModal } from "../../../../../components/ProductCardsGrid/ProductCardsMoreModal";
import { useAuthContext } from "../../../../../components/Auth";
import { ScrollTableWrapper } from "../../../ProductDetailPage/Assets/DocumentsOrDigitalMediaTable/DocumentsOrDigitalMediaTable";
import { borderPrimitiveTokens } from "../../../../../designTokens";

const TableContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.resolve("spacing_lg")};
  overflow: auto;
  & div[class*="TableWrapper"] {
    width: 100%;
    td:last-child:has(> #table-action-buttons) {
      position: sticky;
      right: 0;
    }
    table:has(td:last-child > #table-action-buttons) th:last-child {
      position: sticky;
      right: 0;
    }
  }
`;

const ModifiedTableWrapper = styled(ScrollTableWrapper)`
  z-index: 0;
`;

type CollectionItemsProps = {
  stepItem: TemplateProductFormStepItemSchema;
  onSuccess: () => void;
  isLoading: boolean;
  error?: AxiosError | Error | undefined;
  rows: CollectionValueRowSchema[];
  columns: AttributeSchema[];
  handleCollectionValues: (collectionValues: CollectionValues) => Promise<void>;
  hasReferenceEntityMapping?: boolean;
  collectionId?: string;
};

const Placeholder = (t: TFunction) => (
  <TablePlaceholder
    message={t("No items to show. Please click the button below to add items.")}
  />
);

type TableRow = { [prop: string]: any; uuid: string };

export function CollectionItems({
  stepItem,
  onSuccess,
  isLoading,
  rows,
  columns,
  handleCollectionValues,
  hasReferenceEntityMapping,
  collectionId,
}: CollectionItemsProps) {
  const { t } = useTranslation();
  const [showAddItemForm, setShowAddItemForm] = useState(false);
  const [documentMoreDocs, setDocumentMoreDocs] =
    useState<{
      docs: DocumentCollectionValue[];
      attributeName: string;
    } | null>(null);
  const [objectReferenceMore, setObjectReferenceMore] =
    useState<{
      references: ObjectReferenceResult[];
      attributeName: string;
      targetObjectType?: string;
    } | null>(null);
  const [productReferenceMore, setProductReferenceMore] =
    useState<{
      references: ProductReference[];
      attributeName: string;
    } | null>(null);
  const { adminPath, storePath } = useRoutePath();
  const { roleIsSellerAdmin, user, hasPermission } = useAuthContext();
  const theme = useTheme();
  const [tableData, setTableData] = useState<TableRow[]>([]);
  const [rowToEdit, setRowToEdit] = useState<number | undefined>(undefined);
  const collectionTableRef = useRef<HTMLDivElement>(null);

  const isEditing = useMemo(
    () => !!stepItem.value_rows && rowToEdit !== undefined,
    [rowToEdit, stepItem.value_rows]
  );

  const sortingFn: SortingFn<TableRow> = (rowA, rowB, _columnId) => {
    const valueA =
      rowA.getValue(_columnId) === undefined
        ? 0
        : (rowA.getValue(_columnId) as { key: string })?.key?.split("_")[1] ??
          (rowA.getValue(_columnId) as { name: string }[])[0]?.name ??
          (rowA.getValue(_columnId) as { display_text: string; url: string })
            .display_text ??
          rowA.getValue(_columnId);
    const valueB =
      rowB.getValue(_columnId) === undefined
        ? 0
        : (rowB.getValue(_columnId) as { key: string })?.key?.split("_")[1] ??
          (rowB.getValue(_columnId) as { name: string }[])[0]?.name ??
          (rowB.getValue(_columnId) as { display_text: string; url: string })
            .display_text ??
          rowB.getValue(_columnId);
    if (valueA < valueB) {
      return -1;
    }
    if (valueA > valueB) {
      return 1;
    }
    return 0;
  };

  useEffect(() => {
    if (rows?.length > 0) {
      const dataFromDB = (rows ?? []).reduce<IRow[]>((acc, row) => {
        const temp = row.values.reduce<IRow>(
          (attributeValueAcc, attrValue) => ({
            ...attributeValueAcc,
            [attrValue.attribute_id]: getAttributeValue(
              attrValue,
              attributeValueAcc
            ),
            uuid: row.row_id,
            edit: row.is_editable,
          }),
          {
            uuid: row.row_id,
            edit: false,
            row_editable: true,
            id: `row-${row.row_id}`,
          }
        );
        return row.values.length > 0 ? [...acc, temp] : acc;
      }, []);
      setTableData(dataFromDB);
    } else {
      setTableData([]);
    }
  }, [rows, setTableData]);

  const displayValue = useCallback(
    (
      col: AttributeSchema,
      value: CollectionAttributeDisplayValue,
      isPortfolio?: boolean
    ) =>
      getCollectionAttributeDisplayValue(col, value, {
        isPortfolio,
        theme,
        t,
        user,
        roleIsSellerAdmin,
        hasPermission,
        storePath,
        adminPath,
        onDocumentMoreClick: (docs, attributeName) =>
          setDocumentMoreDocs({ docs, attributeName }),
        onObjectReferenceMoreClick: (
          references,
          attributeName,
          targetObjectType
        ) =>
          setObjectReferenceMore({
            references,
            attributeName,
            targetObjectType,
          }),
        onProductReferenceMoreClick: (references, attributeName) =>
          setProductReferenceMore({ references, attributeName }),
      }),
    [theme, t, user, roleIsSellerAdmin, hasPermission, storePath, adminPath]
  );

  const rowHoverProcessor = useCallback(
    (data: any) => {
      const col_id = data.column.id as string;
      const col = columns.find((col) => col.id === col_id);
      if (col && col.input_type === "single_select") {
        return (data.getValue()?.[0]?.name as string) ?? null;
      }
    },
    [columns]
  );

  const tableColumns = useMemo<ColumnDef<TableRow>[]>(() => {
    const handleRemoveRowFromCollection = async (uuid: UUID) => {
      handleCollectionValues([
        {
          method: "delete",
          collection_id: stepItem.group_or_collection_id,
          row_ids: [uuid],
        },
      ]);
    };

    return columns.reduce<
      {
        header: string | ((props?: any) => JSX.Element);
        accessorKey: string;
        cell?: string | ((props?: any) => JSX.Element);
        enableSorting?: boolean;
        disableResize?: boolean;
        sortingFn?: SortingFn<TableRow>;
      }[]
    >((acc, col, index) => {
      if (index === stepItem.attributes.length - 1) {
        if (col.name.toLocaleLowerCase().includes("description")) {
          acc.push({
            header: col.display_name,
            accessorKey: col.id,
            cell: (cell) => {
              const columnWidth = cell.column.getSize();
              return (
                <div
                  data-for={`desc-${index}-tooltip`}
                  data-tip={cell.getValue()}
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: `${columnWidth}px`,
                  }}
                >
                  {cell.getValue()}
                  <CustomTooltip
                    id={`desc-${index}-tooltip`}
                    place="top"
                    effect="solid"
                    multiline={true}
                  />
                </div>
              );
            },
          });
        } else {
          acc.push({
            header: col.display_name,
            accessorKey: col.id,
            sortingFn: sortingFn,

            cell: ({
              getValue,
            }: {
              getValue: Getter<
                | string
                | null
                | boolean
                | { name: string }[]
                | ProductReference
                | { display_text: string; url: string }
                | null
              >;
            }) => <>{displayValue(col, getValue(), true)}</>,
          });
        }
        acc.push({
          // The empty header and accessor are needed so it doesn't crash.
          header: "",
          enableSorting: false,
          accessorKey: " ",
          disableResize: true,
          cell: (cell) => (
            <IconGroup style={{ justifyContent: "flex-end" }}>
              <ButtonWithConfirmDialog
                Button={(props) => <DeleteButton {...props} />}
                testid={"remove-row-from-collection"}
                type="button"
                handleConfirm={() =>
                  handleRemoveRowFromCollection(cell.row.original.uuid)
                }
                disabled={isLoading}
                confirmMessage={t("Are you sure you want to remove this item?")}
                title={t("Remove Item")}
                confirmBtnLabel={t("Remove")}
                confirmBtnType="destructive"
                useV2Buttons={true}
              />
              <EditButton
                disabled={isLoading}
                onClick={() => {
                  setShowAddItemForm(true);
                  setRowToEdit(cell.row.index);
                }}
                type="button"
              />
            </IconGroup>
          ),
        });
      } else {
        if (col.name.toLocaleLowerCase().includes("description")) {
          acc.push({
            header: col.display_name,
            accessorKey: col.id,
            cell: (cell) => {
              const columnWidth = cell.column.getSize();
              return (
                <div
                  data-for={`desc-${index}-tooltip`}
                  data-tip={cell.getValue()}
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: `${columnWidth}px`,
                  }}
                >
                  {cell.getValue()}
                  <CustomTooltip
                    id={`desc-${index}-tooltip`}
                    place="top"
                    effect="solid"
                    multiline={true}
                  />
                </div>
              );
            },
          });
        } else {
          acc.push({
            header: col.display_name,
            accessorKey: col.id,
            sortingFn: sortingFn,

            cell: ({
              getValue,
            }: {
              getValue: Getter<
                | string
                | null
                | boolean
                | { name: string }[]
                | ProductReference
                | { display_text: string; url: string }
                | null
              >;
            }) => <>{displayValue(col, getValue(), true)}</>,
          });
        }
      }
      return acc;
    }, []);
  }, [
    columns,
    handleCollectionValues,
    stepItem.group_or_collection_id,
    stepItem.attributes.length,
    displayValue,
    isLoading,
    t,
  ]);

  return (
    <div style={{ marginBottom: theme.resolve("spacing_2xl") }}>
      <TableContainer>
        <H5
          style={{
            marginBottom: theme.resolve("spacing_lg"),
            borderBottom: `${borderPrimitiveTokens.border_md} solid ${theme.secondaryBorder}`,
            paddingBottom: theme.resolve("spacing_md"),
          }}
        >
          {stepItem.group_or_collection}
        </H5>
        <ModifiedTableWrapper maxHeight="383px" ref={collectionTableRef}>
          <Table
            columns={tableColumns}
            data={tableData}
            isLoading={isLoading}
            error={undefined}
            Placeholder={Placeholder(t)}
            enableDefaultSort={true}
            rowHover={(data) => rowHover(data, rowHoverProcessor)}
          />
        </ModifiedTableWrapper>
      </TableContainer>
      <PrimaryButtonMediumV2
        type="button"
        onClick={() => {
          setRowToEdit(undefined);
          setShowAddItemForm(true);
        }}
        disabled={
          tableData.length >= stepItem.item_count &&
          stepItem.restrict_items_count
        }
        datafor={`add-item-to-collection-tooltip-${stepItem.id}`}
        datatip={
          tableData.length >= stepItem.item_count &&
          stepItem.restrict_items_count
            ? t(
                "You have reached the maximum number of items for this collection"
              )
            : undefined
        }
      >
        <PlusIcon />
        {t("Add")}
      </PrimaryButtonMediumV2>

      {documentMoreDocs && (
        <DocumentMoreModal
          docs={documentMoreDocs.docs}
          attributeName={documentMoreDocs.attributeName}
          onClose={() => setDocumentMoreDocs(null)}
        />
      )}
      {objectReferenceMore && (
        <ObjectReferenceMoreModal
          references={objectReferenceMore.references}
          attributeName={objectReferenceMore.attributeName}
          targetObjectType={objectReferenceMore.targetObjectType}
          onClose={() => setObjectReferenceMore(null)}
          linkOptions={{ isPortfolio: true, storePath, adminPath }}
          accessOptions={{
            isPortfolio: true,
            user,
            roleIsSellerAdmin,
            hasPermission,
          }}
        />
      )}
      {productReferenceMore && (
        <ProductCardsMoreModal
          references={productReferenceMore.references}
          attributeName={productReferenceMore.attributeName}
          onClose={() => setProductReferenceMore(null)}
          storePath={storePath}
          adminPath={adminPath}
          accessOptions={{
            isPortfolio: true,
            user,
            roleIsSellerAdmin,
            hasPermission,
          }}
        />
      )}
      {createPortal(
        <>
          <SlideOut
            title={isEditing ? t("Edit Item") : t("Add Item")}
            show={showAddItemForm}
            closeFlyout={() => {
              setShowAddItemForm(false);
              setRowToEdit(undefined);
            }}
          >
            <AddItemToCollectionForm
              onSuccess={() => {
                setShowAddItemForm(false);
                setRowToEdit(undefined);
                onSuccess();
              }}
              columns={
                stepItem.columns
                  ?.filter(
                    (col) =>
                      stepItem.attributes.find(
                        (attr) => attr.name === col.name
                      ) !== undefined
                  )
                  .map((col) => ({
                    ...col,
                    is_required:
                      stepItem.attributes.find((attr) => attr.name === col.name)
                        ?.is_required ?? false,
                    is_multiple_choice:
                      stepItem.attributes.find((attr) => attr.name === col.name)
                        ?.is_multiple_choice ?? false,
                  })) ?? []
              }
              row={
                stepItem.value_rows && rowToEdit !== undefined
                  ? stepItem.value_rows[rowToEdit]
                  : null
              }
              stepItem={stepItem}
              handleCollectionValues={handleCollectionValues}
              hasReferenceEntityMapping={hasReferenceEntityMapping}
              collectionId={collectionId}
            />
          </SlideOut>
        </>,
        document.body
      )}
    </div>
  );
}
